// src/components/dashboard/charts/SRSLevelsPieChart.js
import React, { useMemo, useState, useEffect } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { srsMapping } from '../../utils/mappings';
import ChartHeader from '../common/ChartHeader';

// Custom hook to check for screen size
function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [matches, query]);
  return matches;
}

// Tooltip with more thematic labels for an SRS context
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const data = payload[0];
  const total = payload[0].payload.total;
  const percentage = total > 0 ? Math.round((data.value / total) * 100) : 0;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background-secondary p-4 shadow-xl dark:bg-background-secondary animate-fade-in">
      <div className="relative z-10">
        <div className="mb-3 flex items-center gap-3">
          <div className="h-4 w-4 rounded-full shadow-sm" style={{ backgroundColor: data.payload.color }} />
          <span className="font-semibold text-content-primary">{data.payload.name}</span>
        </div>
        <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
          <span className="text-content-secondary">Items:</span>
          <span className="text-right font-bold text-content-primary">{data.value}</span>
          <span className="text-content-secondary">Percentage:</span>
          <span className="text-right font-bold text-content-primary">{percentage}%</span>
        </div>
      </div>
    </div>
  );
};

// The original Pie Chart view for Desktop
const PieChartView = ({ chartData, totalItems, type }) => (
  <div className="flex h-full flex-col justify-between">
    <div>
      <div className="relative z-10 h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              cornerRadius={3}
              stroke="none"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} className="drop-shadow-sm transition-opacity duration-200 hover:opacity-80" />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="flex h-28 w-28 flex-col items-center justify-center rounded-full border border-border bg-background-secondary/30 text-center backdrop-blur-md dark:bg-background-secondary/30">
            <div className="mb-1 text-3xl font-black text-content-primary">{totalItems}</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-content-secondary">{type === 'sentences' ? 'Sentences' : 'Words'}</div>
          </div>
        </div>
      </div>
    </div>
    <Legend chartData={chartData} />
  </div>
);

// NEW: A compact stacked bar view for Mobile
const CompactSRSView = ({ chartData, totalItems }) => (
  <div className="flex h-full flex-col justify-between">
    <div>
      <div className="mb-4 text-center">
        <span className="text-4xl font-extrabold text-content-primary">{totalItems}</span>
      </div>
      <div className="flex h-5 w-full overflow-hidden rounded-full border border-border bg-background-primary shadow-inner">
        {chartData.map((entry) => (
          <div
            key={entry.name}
            className="h-full transition-all duration-300"
            style={{
              width: `${(entry.value / totalItems) * 100}%`,
              backgroundColor: entry.color,
            }}
          />
        ))}
      </div>
    </div>
    <Legend chartData={chartData} />
  </div>
);

// Shared Legend component
const Legend = ({ chartData }) => (
  <div className="relative z-10 mt-6">
    <div className="overflow-hidden rounded-xl border border-border bg-background-secondary/40 p-4 backdrop-blur-xl dark:bg-background-secondary/40">
      <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-50" />
      <div className="relative flex min-h-[88px] flex-wrap items-center justify-center gap-2">
        {chartData.map((entry, index) => (
          <div
            key={index}
            className="flex items-center gap-2 rounded-full border border-border bg-background-secondary/60 px-3 py-2 backdrop-blur-lg transition-transform duration-200 hover:scale-105 active:scale-95 cursor-pointer animate-fade-in"
            style={{ animationDelay: `${index * 100}ms`, opacity: 0 }}
          >
            <div className="h-3 w-3 rounded-full shadow-sm" style={{ backgroundColor: entry.color }} />
            <span className="text-xs font-medium text-content-secondary hidden sm:inline">{entry.name}</span>
            <span className="text-xs font-bold text-content-primary">{entry.value}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SRSLevelsPieChart = ({ data, type = 'sentences' }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { chartData, totalItems, hasData } = useMemo(() => {
    const total = Object.values(data || {}).reduce((sum, value) => sum + (value || 0), 0);
    const processed = Object.keys(srsMapping)
      .map((key) => ({
        name: srsMapping[key],
        value: data?.[key] ?? 0,
        color: `var(--color-srs-${key})`,
        srsLevel: key,
        total: total,
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => a.srsLevel - b.srsLevel); // Sort by SRS level

    return {
      chartData: processed,
      totalItems: total,
      hasData: processed.length > 0,
    };
  }, [data]);

  // Glassmorphic empty state
  if (!hasData) {
    return (
      <div className="flex h-full w-full flex-col animate-fade-in">
        <div className="relative flex flex-grow flex-col overflow-hidden rounded-2xl border-2 border-border bg-background-secondary p-6 backdrop-blur-xl dark:bg-background-secondary">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute right-0 top-0 h-32 w-32 -translate-y-16 translate-x-16 rounded-full bg-gradient-to-br from-primary to-secondary" />
            <div className="absolute bottom-0 left-0 h-24 w-24 translate-y-12 -translate-x-12 rounded-full bg-gradient-to-br from-accent to-primary" />
          </div>
          <div className="relative z-10">
            <ChartHeader title={`${type.charAt(0).toUpperCase() + type.slice(1)} Distribution`} />
          </div>
          <div className="relative z-10 flex flex-grow flex-col items-center justify-center text-center text-content-secondary">
            <div className="relative mb-4 flex h-20 w-20 items-center justify-center rounded-full border-4 border-white/50 bg-gradient-to-br from-gray-100 to-gray-200 backdrop-blur-sm dark:from-gray-700 dark:to-gray-800">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-500 dark:to-gray-600" />
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-gray-400 opacity-50" />
            </div>
            <p className="mb-2 text-base font-semibold">No progress data yet</p>
            <p className="text-center text-sm opacity-80">
              {type === 'sentences' ? 'Complete sentence reviews' : 'Complete word reviews'} to see your progress
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col animate-fade-in">
      <div className="relative flex flex-grow flex-col overflow-hidden rounded-2xl border-2 border-border bg-background-secondary/70 p-6 backdrop-blur-xl dark:bg-background-secondary/50">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50" />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute right-0 top-0 h-32 w-32 -translate-y-16 translate-x-16 rounded-full bg-gradient-to-br from-primary to-secondary" />
          <div className="absolute bottom-0 left-0 h-24 w-24 translate-y-12 -translate-x-12 rounded-full bg-gradient-to-br from-accent to-primary" />
        </div>

        <div className="relative z-10">
          <ChartHeader title={`${type.charAt(0).toUpperCase() + type.slice(1)} Distribution`} />
        </div>

        {/* Conditionally render the correct view based on screen size */}
        {isMobile ? (
          <CompactSRSView chartData={chartData} totalItems={totalItems} />
        ) : (
          <PieChartView chartData={chartData} totalItems={totalItems} type={type} />
        )}
      </div>
    </div>
  );
};

export default SRSLevelsPieChart;
